/**
 * PAL Knowledge Concepts — interactive module concept map.
 *
 * Data flow:
 * 1. Load graph + module mastery + session “next action” (same session as `sessionId` query).
 * 2. Build ordered nodes (topological + optional remediation insert).
 * 3. Assign each node `conceptStatus` (module `concept_status`)
 *    and optional session `navHighlight` for pills / emphasis only.
 * 4. Layout in a zig-zag grid; edges are decorative SVG lines between consecutive laid-out nodes.
 *
 * Re-exports {@link IKnowledgeConcept} for `../api/module` compatibility.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  getKnowledgeConceptGraph,
  getModuleMasteryApi,
  IModuleMasteryData,
} from '../api/module';
import {
  abandonModuleSessionApi,
  createModuleSessionApi,
  endModuleSessionApi,
  getModuleSessionHistoryApi,
  getSessionRecommendationNextAction,
  SessionRecommendationNextActionResponse,
} from '../api/learners';
import { handleKnowledgeConceptsNavigation } from './knowledgeConceptsNavigationUtil';
import {
  HOME,
  PAL_KNOWLEDGE_CONCEPTS,
} from '../../../routeHandling/RoutesNomenclature';

import {
  buildOrderedConceptNodes,
  getConceptMasteryStatusForNode,
  getNavHighlightForGraph,
} from './knowledgeConcepts.graph';
import {
  KnowledgeConceptsPanel,
  MODULE_MASTERED_EMOJI,
  PANEL_DEFAULT_EMOJI,
} from './KnowledgeConceptsPanel';
import {
  getKnowledgeConceptBadgeLabel,
  masterySequenceBadgeColors,
  masteryStatusConfig,
  shouldShowConceptStatusBadge,
} from './knowledgeConcepts.status';
import type { IKnowledgeConcept, LayoutNode } from './knowledgeConcepts.types';
import {
  useAppDispatch,
  useAppSelector,
} from 'apps/student/src/reduxStore/reduxHooks';
import { usePalBackNavigationHandlers } from '../hooks/usePalBackNavigationHandlers';
import { deserify, useLoadingTimer } from '@geneo2-web/shared-ui';
import PalLoader from '../components/PalLoader';
import { setModuleRedirectionPath } from '../reducer/pal.slice';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';

export type { IKnowledgeConcept } from './knowledgeConcepts.types';

// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------

const PANEL_WIDTH = 380;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const PalKnowledgeConcepts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { external_module_id } = useParams();
  const sessionId =
    new URLSearchParams(location.search).get('sessionId') || undefined;
  const dispatch = useAppDispatch();

  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const cols = isLgUp ? 5 : isMdUp ? 4 : 2;

  // --- Remote data ----------------------------------------------------------
  const [loading, setLoading] = useState<
    'completed' | 'loading' | 'error' | 'error-create' | 'error-module'
  >('loading');
  const [isLoaderLoaded, setIsLoaderLoaded] = useState(false);
  const [knowledgeConcepts, setKnowledgeConcepts] = useState<IKnowledgeConcept>(
    {}
  );
  const [sessionRecommendationAction, setSessionRecommendationAction] =
    useState<SessionRecommendationNextActionResponse | undefined>(undefined);
  const [moduleMastery, setModuleMastery] = useState<
    IModuleMasteryData | undefined
  >(undefined);
  const { moduleRedirectionPath } = useAppSelector((state) =>
    deserify(state.pal)
  );

  const backButtonClick = (clickedFromUI?: boolean) =>
    useCallback(
      async (isHome?: true) => {
        if (moduleRedirectionPath) {
          navigate(moduleRedirectionPath);
          dispatch(setModuleRedirectionPath(undefined));
        } else if (clickedFromUI) {
          navigate(-1);
          return;
        }
        try {
          setLoading('loading');
          if (sessionId) {
            await endModuleSessionApi({ session_id: sessionId });
          }
        } catch (err) {
          console.error(err);
        } finally {
          if (isHome) {
            navigate(HOME, {
              replace: true,
            });
          }
        }
      },
      [sessionId, navigate]
    );

  const backButtonClickDirect = backButtonClick();
  const backButtonClickUI = backButtonClick(true);
  usePalBackNavigationHandlers(backButtonClickDirect, [sessionId]);

  useEffect(() => {
    getKnowledgeConcept();
  }, [sessionId]);

  /** Abandon stale in-progress sessions before retrying create (retry path). */
  const getOngoingSessionsAndAbandon = async () => {
    try {
      if (!external_module_id) return;
      const res = await getModuleSessionHistoryApi({
        module_id: external_module_id,
        status: 'in_progress',
      });
      for (const session of res.sessions || []) {
        await abandonModuleSessionApi({ session_id: session.id });
      }
    } catch (err) {
      console.log(err);
    }
  };

  const createModuleSession = async (numberOfRetries = 1) => {
    if (!external_module_id) {
      setLoading('error-module');
      return;
    }
    try {
      const res = await createModuleSessionApi({
        module_id: external_module_id,
      });
      const sessionId = res?.id;
      if (sessionId) {
        navigate(
          `${PAL_KNOWLEDGE_CONCEPTS}/${external_module_id}?sessionId=${sessionId}`,
          { replace: true }
        );
      } else {
        setLoading('error-create');
      }
    } catch (err) {
      console.log(err);
      if (numberOfRetries > 0) {
        await getOngoingSessionsAndAbandon();
        createModuleSession(numberOfRetries - 1);
      } else {
        setLoading('error-create');
      }
    }
  };

  const getKnowledgeConcept = async () => {
    try {
      setLoading('loading');
      if (!external_module_id) {
        setLoading('error-module');
        return;
      }
      if (!sessionId) {
        createModuleSession();
        return;
      }
      const res = await getKnowledgeConceptGraph({
        module_id: external_module_id,
      });
      const moduleMasteryRes = await getModuleMasteryApi({
        module_id: external_module_id,
      });
      setModuleMastery(moduleMasteryRes.data);

      const recommendationRes = await getSessionRecommendationNextAction({
        session_id: sessionId,
      });
      setSessionRecommendationAction(recommendationRes);
      setKnowledgeConcepts(res.data);
      setLoading('completed');
    } catch (err) {
      setLoading('error');
    }
  };

  // --------------------------------------------------

  const showInvalidSessionToast = useCallback(() => {
    dispatch(
      setToastInfo({
        label: t('PAL_INVALID_SESSION_TOAST'),
        variant: 'error',
        open: true,
      })
    );
  }, [dispatch, t]);

  // --------------------------------------------------

  const handleButtonAction = async () => {
    if (!sessionId || !external_module_id) return;

    const nav = sessionRecommendationAction?.navigation;
    const actionTargetId =
      nav?.next_concept_id ?? sessionRecommendationAction?.concept_id;
    const defaultSelectedId =
      (actionTargetId
        ? layoutNodes.find((n) => n.id === actionTargetId)?.id
        : undefined) ?? layoutNodes[0]?.id;
    const followRecommendedJourney =
      selectedNodeId &&
      (sessionRecommendationAction?.recommend_diagnostic
        ? false
        : defaultSelectedId == selectedNodeId);
    if (selectedNodeId && !followRecommendedJourney) {
      console.log('force');
      await handleKnowledgeConceptsNavigation({
        sessionId,
        moduleId: external_module_id,
        navigateToKnowledgeGraph: false,
        decision: 'force-update',
        nextConceptId: selectedNodeId,
        navigate,
        setSessionRecommendationAction,
        skipDiagnositc: true,
        isCrossModuleRemediation: orderedNodes.find(
          (n) => n.id === selectedNodeId
        )?.is_cross_module,
        moduleExitFn: backButtonClickUI,
        onInvalidSession: showInvalidSessionToast,
      });
    } else {
      console.log('auto');
      await handleKnowledgeConceptsNavigation({
        sessionId,
        moduleId: external_module_id,
        navigateToKnowledgeGraph: false,
        decision: sessionRecommendationAction?.navigation?.decision,
        nextConceptId: sessionRecommendationAction?.navigation?.next_concept_id,
        navigate,
        setSessionRecommendationAction,
        isCrossModuleRemediation:
          sessionRecommendationAction?.navigation?.is_cross_module_remediation,
        moduleExitFn: backButtonClickUI,
        onInvalidSession: showInvalidSessionToast,
      });
    }
  };

  // --- Canvas / selection ---------------------------------------------------
  const canvasRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ w: 900, h: 500 });
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const hasData =
    Array.isArray(knowledgeConcepts.nodes) &&
    knowledgeConcepts.nodes.length > 0 &&
    Array.isArray(knowledgeConcepts.topological_order) &&
    knowledgeConcepts.topological_order.length > 0;

  useEffect(() => {
    const measure = () => {
      if (canvasRef.current) {
        setCanvasSize({
          w: canvasRef.current.clientWidth,
          h: canvasRef.current.clientHeight,
        });
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [loading]);

  const orderedNodes = useMemo(
    () =>
      buildOrderedConceptNodes(
        hasData,
        knowledgeConcepts,
        sessionRecommendationAction,
        external_module_id
      ),
    [
      hasData,
      knowledgeConcepts.nodes?.map((val) => val.id).join(','),
      knowledgeConcepts.topological_order?.join(','),
      JSON.stringify(sessionRecommendationAction),
      external_module_id,
    ]
  );

  /** Assign coordinates + status for each node in `orderedNodes`. */
  const layoutNodes: LayoutNode[] = useMemo(() => {
    const spacingX = Math.max(120, (canvasSize.w - 80) / Math.max(cols, 1));
    const spacingY = isMdUp ? 200 : 170;
    const startX = 40;
    /** Extra top offset so the graph sits slightly below the header / canvas top */
    const startY = isMdUp ? 112 : 110;

    return orderedNodes.map((node, i) => {
      const row = Math.floor(i / cols);
      const col = i % cols;
      const actualCol = row % 2 === 0 ? col : cols - 1 - col;
      const verticalOffset = (actualCol % 2) * 20;
      const conceptStatus = getConceptMasteryStatusForNode(
        node.id,
        moduleMastery
      );
      const navHighlight = getNavHighlightForGraph(
        node.id,
        sessionRecommendationAction
      );

      return {
        ...node,
        conceptStatus,
        navHighlight,
        seqIndex: i,
        x: startX + actualCol * spacingX + spacingX / 2,
        y: startY + row * spacingY + verticalOffset,
      };
    });
  }, [
    orderedNodes,
    canvasSize,
    cols,
    isMdUp,
    moduleMastery,
    sessionRecommendationAction,
  ]);

  const canvasHeight = useMemo(() => {
    if (layoutNodes.length === 0) return 500;
    const maxY = Math.max(...layoutNodes.map((n) => n.y));
    return maxY + 200;
  }, [layoutNodes]);

  /**
   * Default selection: `next_concept_id` if present, else `concept_id`.
   * User tap overrides until session data changes.
   */
  const selectedNode = useMemo(() => {
    const nav = sessionRecommendationAction?.navigation;
    const actionTargetId =
      nav?.next_concept_id ?? sessionRecommendationAction?.concept_id;
    const isDiagnostic = sessionRecommendationAction?.recommend_diagnostic;
    const isModuleComplete = nav?.decision === 'module_complete';
    if ((isDiagnostic || isModuleComplete) && !selectedNodeId) {
      return undefined;
    }

    return (
      layoutNodes.find((n) => n.id === selectedNodeId) ??
      (actionTargetId
        ? layoutNodes.find((n) => n.id === actionTargetId)
        : undefined) ??
      layoutNodes[0]
    );
  }, [layoutNodes, selectedNodeId, sessionRecommendationAction]);

  const selectedNodeSeqIndex = useMemo(
    () =>
      selectedNode
        ? layoutNodes.findIndex((n) => n.id === selectedNode.id)
        : -1,
    [layoutNodes, selectedNode?.id]
  );

  const panelBadgeLabel = useMemo((): string | null => {
    if (
      sessionRecommendationAction?.recommend_diagnostic &&
      selectedNodeId === null
    ) {
      return null;
    }
    if (!selectedNode || !shouldShowConceptStatusBadge(selectedNode)) {
      return null;
    }
    return t(
      getKnowledgeConceptBadgeLabel(
        selectedNode,
        selectedNodeSeqIndex >= 0 ? selectedNodeSeqIndex : 0,
        sessionRecommendationAction
      )
    );
  }, [
    selectedNode,
    selectedNodeSeqIndex,
    sessionRecommendationAction,
    selectedNodeId,
  ]);

  const diagnosticDefaultPanel =
    sessionRecommendationAction?.recommend_diagnostic === true &&
    selectedNodeId === null;

  const isModuleMastered =
    sessionRecommendationAction?.navigation?.decision === 'module_complete' &&
    selectedNodeId === null;

  const handleCanvasBackgroundClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  const handleNodeClick = useCallback(
    (node: LayoutNode) => {
      setSelectedNodeId(node.id);

      // if (!isMdUp) setDrawerOpen(true);
    },
    [isMdUp, layoutNodes, sessionRecommendationAction]
  );

  // --- Progress header ------------------------------------------------------
  const masteredCount = layoutNodes.filter(
    (n) => n.conceptStatus === 'mastered'
  ).length;
  const totalModules = layoutNodes?.length || 0;
  const progressPct =
    totalModules > 0 ? (masteredCount / totalModules) * 100 : 0;

  const cfg = selectedNode
    ? masteryStatusConfig[selectedNode.conceptStatus]
    : masteryStatusConfig.not_assessed;

  const panelBadgePillStyle = selectedNode?.navHighlight
    ? {
        badgeBg: masteryStatusConfig[selectedNode.conceptStatus].badgeBg,
        badgeColor: masteryStatusConfig[selectedNode.conceptStatus].badgeColor,
      }
    : null;

  // --- Early states ---------------------------------------------------------
  // Keep PalLoader mounted until its delay finishes, even if data already loaded.
  if (loading === 'loading' || !isLoaderLoaded) {
    return (
      <PalLoader
        contentLockKey={loading}
        emoji="🧠"
        heading={t('PAL_KC_LOADER_HEADING')} // discuss
        subHeading={t('PAL_KC_LOADER_SUBHEADING')}
        messages={[
          t('PAL_KC_LOADER_MSG_1'),
          t('PAL_KC_LOADER_MSG_2'),
          t('PAL_KC_LOADER_MSG_3'),
        ]}
        isLoaderLoaded={isLoaderLoaded}
        setIsLoaderLoaded={setIsLoaderLoaded}
        loadedAfterMs={2000}
      />
    );
  }

  if (loading !== 'completed') {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f8fafc',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {loading == 'error-create' ? (
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1e293b' }}>
            {t('PAL_KC_ERROR_CREATE')}
          </Typography>
        ) : loading == 'error-module' ? (
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1e293b' }}>
            {t('PAL_KC_ERROR_MODULE')}
          </Typography>
        ) : loading == 'error' ? (
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1e293b' }}>
            {t('PAL_KC_ERROR_CONCEPTS')}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1e293b' }}>
            {t('PAL_KC_NO_CONCEPTS')}
          </Typography>
        )}
      </Box>
    );
  }

  const getButtonLabel = () => {
    if (selectedNode?.navHighlight === 'recommended') {
      switch (sessionRecommendationAction?.recommended_action) {
        case 'answer_challenge_question':
          t('PAL_KC_BTN_START_CHALLENGE');
        case 'view_content':
          t('PAL_KC_BTN_VIEW_CONTENT');
        case 'start_challenge':
          t('PAL_KC_BTN_START_CHALLENGE');
      }
    }
    return t('PAL_KC_BTN_START_PRACTISING');
  };

  // --- Main layout ----------------------------------------------------------
  return (
    <Box
      sx={{
        width: '100%',
        height: { xs: 'auto', md: '100vh' },
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        bgcolor: '#f8fafc',
        overflow: { xs: 'visible', md: 'hidden' },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(circle at center, rgba(148,163,184,0.3) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          pointerEvents: 'none',
          zIndex: 0,
        },
      }}
    >
      {/*
        Header sits outside the scroll row so it is not covered by siblings
        (fixed panel + map nodes both used z-index 100). Keep it above the panel.
      */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 2.5 },
          bgcolor: 'white',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          gap: { xs: 2, sm: 3 },
          flexShrink: 0,
          position: 'relative',
          zIndex: 110,
          mr: { xs: 0, md: `${PANEL_WIDTH}px` },
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <IconButton
            onClick={() => backButtonClickUI()}
            size="small"
            sx={{
              color: '#0f172a',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.06)' },
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>

          <Box
            sx={{
              flex: 1,
              pl: 2.5,
              borderLeft: '3px solid #f59e0b',
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 18, md: 24 },
                fontWeight: 700,
                color: '#0f172a',
                letterSpacing: -0.5,
                lineHeight: 1.2,
              }}
            >
              {moduleMastery?.module_name}
            </Typography>
            <Typography
              sx={{ fontSize: { xs: 12, md: 14 }, color: '#64748b', mt: 0.5 }}
            >
              {t('PAL_KC_HEADER_SUBTITLE')}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minWidth: { xs: '100%', sm: 180 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: '#94a3b8',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                mr: '5px',
              }}
            >
              {t('PAL_KC_PROGRESS_LABEL')}
            </Typography>
            <Typography
              sx={{ fontSize: 12, fontWeight: 600, color: '#10b981' }}
            >
              {t('PAL_KC_CONCEPTS_MASTERED', {
                count: masteredCount,
                total: layoutNodes.length,
              })}
            </Typography>
          </Box>
          <Box
            sx={{
              width: '100%',
              height: 6,
              bgcolor: '#e2e8f0',
              borderRadius: 100,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${progressPct}%`,
                background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)',
                borderRadius: 100,
                transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
                boxShadow: '0 0 10px rgba(16,185,129,0.5)',
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }}
      >
        {/* ----- Scrollable canvas (desktop); header no longer inside this column ----- */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            minHeight: 0,
          }}
        >
          <Box
            ref={canvasRef}
            sx={{
              flex: 1,
              minHeight: 0,
              mr: { xs: 0, md: `${PANEL_WIDTH}px` },
              position: 'relative',
              overflowY: { xs: 'visible', md: 'auto' },
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 1,
            }}
            onClick={handleCanvasBackgroundClick}
          >
            <Box
              role="presentation"
              sx={{
                position: 'relative',
                width: '100%',
                minHeight: canvasHeight,
                my: { xs: 0, md: 'auto' },
              }}
            >
              <KnowledgeConceptEdges
                layoutNodes={layoutNodes}
                canvasHeight={canvasHeight}
              />
              <KnowledgeConceptNodes
                layoutNodes={layoutNodes}
                selectedNode={selectedNode}
                sessionRecommendationAction={sessionRecommendationAction}
                onNodeClick={handleNodeClick}
                currentModuleId={external_module_id}
              />
            </Box>
          </Box>
        </Box>

        {/* ----- Desktop side panel ----- */}
        <Box
          sx={{
            position: 'fixed',
            right: 0,
            top: 0,
            width: PANEL_WIDTH,
            height: '100vh',
            bgcolor: 'white',
            borderLeft: '1px solid #e2e8f0',
            boxShadow: '-8px 0 32px rgba(0,0,0,0.06)',
            zIndex: 100,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <KnowledgeConceptsPanel
            selectedNode={selectedNode}
            cfg={cfg}
            badgeLabel={
              selectedNode?.conceptStatus
                ? t(
                    masteryStatusConfig[selectedNode.conceptStatus]
                      ?.badgeTextKey
                  )
                : null
            }
            badgeEmoji={
              selectedNode?.conceptStatus
                ? masteryStatusConfig[selectedNode.conceptStatus]?.badgeEmoji
                : undefined
            }
            badgePillStyle={panelBadgePillStyle}
            handleButtonAction={handleButtonAction}
            diagnosticDefaultPanel={diagnosticDefaultPanel}
            moduleName={moduleMastery?.module_name}
            isRecommended={selectedNode?.navHighlight === 'recommended'}
            onRecommendedPathClick={handleCanvasBackgroundClick}
            conceptsCount={layoutNodes.length}
            buttonLabel={getButtonLabel()}
            isModuleMastered={isModuleMastered}
            modulesMasteredCount={masteredCount}
            totalModulesCount={totalModules}
            moduleProgress={progressPct}
          />
        </Box>

        {/* ----- Mobile drawer ----- */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: { xs: '85vw', sm: PANEL_WIDTH },
              boxSizing: 'border-box',
            },
          }}
        >
          <IconButton
            onClick={() => setDrawerOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              bgcolor: 'rgba(255,255,255,0.9)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              width: 32,
              height: 32,
              fontSize: 18,
              '&:hover': { bgcolor: 'white' },
            }}
          >
            ✕
          </IconButton>
          <KnowledgeConceptsPanel
            selectedNode={selectedNode}
            cfg={cfg}
            badgeLabel={
              selectedNode?.conceptStatus
                ? t(
                    masteryStatusConfig[selectedNode.conceptStatus]
                      ?.badgeTextKey
                  )
                : null
            }
            badgeEmoji={
              selectedNode?.conceptStatus
                ? masteryStatusConfig[selectedNode.conceptStatus]?.badgeEmoji
                : undefined
            }
            badgePillStyle={panelBadgePillStyle}
            handleButtonAction={handleButtonAction}
            diagnosticDefaultPanel={diagnosticDefaultPanel}
            moduleName={moduleMastery?.module_name}
            isRecommended={selectedNode?.navHighlight === 'recommended'}
            onRecommendedPathClick={handleCanvasBackgroundClick}
            conceptsCount={layoutNodes.length}
            buttonLabel={getButtonLabel()}
            isModuleMastered={isModuleMastered}
            modulesMasteredCount={masteredCount}
            totalModulesCount={totalModules}
            moduleProgress={progressPct}
          />
        </Drawer>
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: '24px',
          left: '24px',
          width: { xs: 'calc(100% - 48px)', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          zIndex: 101,
        }}
      >
        <Button
          variant="contained"
          onClick={() => setDrawerOpen(true)}
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            mx: 'auto',
            px: 5,
            py: 1.25,
            gap: 1,
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 15,
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: '#0a0a0f',
            boxShadow: '0 6px 24px rgba(245,158,11,0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
              boxShadow: '0 8px 28px rgba(245,158,11,0.45)',
            },
          }}
        >
          <Box component="span" sx={{ fontSize: 22, lineHeight: 1 }}>
            {diagnosticDefaultPanel
              ? PANEL_DEFAULT_EMOJI
              : isModuleMastered
              ? MODULE_MASTERED_EMOJI
              : selectedNode?.emoji || PANEL_DEFAULT_EMOJI}
          </Box>
          {isModuleMastered
            ? t('PAL_KC_CTA_VIEW_OTHER_CHAPTERS')
            : t('PAL_KC_CTA_START')}
        </Button>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 2,
            bgcolor: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: 3,
            px: 2.5,
            py: 1.5,
            boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
          }}
        >
          {/* | 'not_assessed'
  | 'struggling'
  | 'beginning'
  | 'developing'
  | 'confident'
  | 'mastered'; */}
          {[
            {
              color: masterySequenceBadgeColors.not_assessed,
              label: 'PAL_KC_LEGEND_NOT_ASSESSED',
            },
            {
              color: masterySequenceBadgeColors.struggling,
              label: 'PAL_KC_LEGEND_STRUGGLING',
            },
            {
              color: masterySequenceBadgeColors.developing,
              label: 'PAL_KC_LEGEND_DEVELOPING',
            },
            {
              color: masterySequenceBadgeColors.confident,
              label: 'PAL_KC_LEGEND_CONFIDENT',
            },
            {
              color: masterySequenceBadgeColors.mastered,
              label: 'PAL_KC_LEGEND_MASTERED',
            },
            // { color: masterySequenceBadgeColors.beginning, label: 'Beginning' },
          ].map((item) => (
            <Stack
              key={item.label}
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: item.color,
                  boxShadow: `0 0 0 2px ${item.color}33`,
                }}
              />
              <Typography
                sx={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}
              >
                {t(item.label)}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Subcomponents (keeps main render scannable)
// ---------------------------------------------------------------------------

function KnowledgeConceptEdges({
  layoutNodes,
  canvasHeight,
}: {
  layoutNodes: LayoutNode[];
  canvasHeight: number;
}) {
  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: canvasHeight,
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      <defs>
        <linearGradient id="connCompleted" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop
            offset="0%"
            style={{ stopColor: '#10b981', stopOpacity: 0.8 }}
          />
          <stop
            offset="100%"
            style={{ stopColor: '#34d399', stopOpacity: 0.8 }}
          />
        </linearGradient>
        <linearGradient id="connRecommended" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
          <stop
            offset="100%"
            style={{ stopColor: '#fbbf24', stopOpacity: 1 }}
          />
        </linearGradient>
      </defs>
      {layoutNodes.map((node, i) => {
        if (i >= layoutNodes.length - 1) return null;
        const next = layoutNodes[i + 1];

        const nextStrugglingNav = next.navHighlight === 'struggling';
        const bothMastered =
          node.conceptStatus === 'mastered' &&
          next.conceptStatus === 'mastered';
        const nodeIsRemediationTarget =
          node.navHighlight === 'remediation-target';
        const toRecommendedLike =
          next.navHighlight === 'recommended' ||
          next.navHighlight === 'remediation-target';

        let stroke: string = '#cbd5e1';
        let strokeWidth = 2;
        let dasharray = '8 6';

        if (nextStrugglingNav) {
          stroke = '#ef4444';
          strokeWidth = 3;
          dasharray = '0';
        } else if (toRecommendedLike) {
          stroke = 'url(#connRecommended)';
          strokeWidth = 3;
          dasharray = node.navHighlight === 'struggling' ? '8 6' : '0';
        } else if (bothMastered) {
          stroke = 'url(#connCompleted)';
          strokeWidth = 2.5;
          dasharray = '0';
        } else if (nodeIsRemediationTarget) {
          stroke = 'url(#connRecommended)';
          strokeWidth = 2;
          dasharray = '8 6';
        }

        return (
          <line
            key={`conn-${node.id}-${next.id}`}
            x1={node.x}
            y1={node.y}
            x2={next.x}
            y2={next.y}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeDasharray={dasharray}
            strokeLinecap="round"
            opacity={0.8}
          />
        );
      })}
    </svg>
  );
}

function KnowledgeConceptNodes({
  layoutNodes,
  selectedNode,
  sessionRecommendationAction,
  onNodeClick,
  currentModuleId,
}: {
  layoutNodes: LayoutNode[];
  selectedNode: LayoutNode | undefined;
  sessionRecommendationAction:
    | SessionRecommendationNextActionResponse
    | undefined;
  onNodeClick: (node: LayoutNode) => void;
  currentModuleId?: string;
}) {
  const { t } = useTranslation();
  return (
    <>
      {layoutNodes.map((node, i) => {
        const sc = masteryStatusConfig[node.conceptStatus];
        const isNavEmphasis =
          node.navHighlight === 'recommended' ||
          node.navHighlight === 'remediation-target';
        const isSelected = selectedNode?.id === node.id;
        const circleSize = isSelected
          ? { xs: 86, md: 96 }
          : isNavEmphasis
          ? { xs: 78, md: 88 }
          : { xs: 64, md: 72 };
        const iconSize = isSelected
          ? { xs: 36, md: 40 }
          : isNavEmphasis
          ? { xs: 32, md: 36 }
          : { xs: 26, md: 30 };
        const heartbeatKeyframeName = `palHeartbeat_${node.conceptStatus}`;

        return (
          <Box
            key={node.id}
            onClick={(e) => {
              e.stopPropagation();
              onNodeClick(node);
            }}
            sx={{
              position: 'absolute',
              left: node.x,
              top: node.y,
              transform: isSelected
                ? 'translate(-50%, -50%) scale(1.12)'
                : 'translate(-50%, -50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              transition: 'all 0.35s cubic-bezier(0.34,1.56,0.64,1)',
              zIndex: isSelected ? 120 : isNavEmphasis ? 50 : 2,
              '&:hover': isSelected
                ? {
                    transform: 'translate(-50%, -50%) scale(1.12)',
                    zIndex: 120,
                  }
                : {
                    transform: 'translate(-50%, -50%) scale(1.08)',
                    zIndex: 100,
                  },
            }}
          >
            {shouldShowConceptStatusBadge(node) && node.navHighlight && (
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: -34, md: -30, lg: -40 },
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: masteryStatusConfig[node.conceptStatus]?.badgeBg,
                  color: masteryStatusConfig[node.conceptStatus]?.badgeColor,
                  px: { xs: 1, md: 1.75 },
                  py: { xs: 0.5, md: 0.75 },
                  borderRadius: 100,
                  fontSize: { xs: 8, md: 10 },
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  whiteSpace: 'nowrap',
                  boxShadow: `0 4px 20px ${sc.borderColor}66`,
                  zIndex: 10,
                }}
              >
                {t(
                  getKnowledgeConceptBadgeLabel(
                    node,
                    i,
                    sessionRecommendationAction
                  )
                )}
              </Box>
            )}

            <Box
              sx={{
                position: 'absolute',
                top: -6,
                left: -6,
                width: isNavEmphasis ? 28 : 24,
                height: isNavEmphasis ? 28 : 24,
                borderRadius: '50%',
                bgcolor: masterySequenceBadgeColors[node.conceptStatus],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isNavEmphasis ? 12 : 11,
                fontWeight: 700,
                color: 'white',
                zIndex: 10,
                pointerEvents: 'none',
                boxShadow: `0 2px 6px ${
                  masterySequenceBadgeColors[node.conceptStatus]
                }66`,
                border: '2px solid white',
              }}
            >
              {i + 1}
            </Box>

            {!!masteryStatusConfig[node.conceptStatus]?.badgeEmoji && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -6,
                  right: -6,
                  width: isNavEmphasis ? 28 : 24,
                  height: isNavEmphasis ? 28 : 24,
                  borderRadius: '50%',
                  bgcolor: masterySequenceBadgeColors[node.conceptStatus],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isNavEmphasis ? 12 : 11,
                  fontWeight: 700,
                  color: 'white',
                  zIndex: 10,
                  pointerEvents: 'none',
                  boxShadow: `0 2px 6px ${
                    masterySequenceBadgeColors[node.conceptStatus]
                  }66`,
                  border: '2px solid white',
                }}
              >
                {masteryStatusConfig[node.conceptStatus]?.badgeEmoji}
              </Box>
            )}

            <Box
              sx={{
                width: circleSize,
                height: circleSize,
                borderRadius: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: sc.circleBg,
                border: `${isNavEmphasis ? 3 : 2}px solid ${sc.borderColor}`,
                boxShadow: sc.shadow,
                transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                position: 'relative',
                ...(isSelected
                  ? {
                      animation: `${heartbeatKeyframeName} 1.2s ease-in-out infinite`,
                      [`@keyframes ${heartbeatKeyframeName}`]: {
                        '0%, 100%': {
                          boxShadow: sc.shadow,
                        },
                        '50%': {
                          boxShadow: `0 0 0 10px ${sc.borderColor}22, ${sc.shadow}`,
                        },
                      },
                    }
                  : {}),
              }}
            >
              <Typography
                sx={{
                  fontSize: iconSize,
                  lineHeight: 1,
                  transition: 'all 0.3s ease',
                }}
              >
                {node.emoji}
              </Typography>
            </Box>

            <Box
              sx={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                mt: 2,
                px: 1.75,
                py: 1.25,
                bgcolor: 'white',
                background: sc.labelBg,
                border: `1px solid ${sc.labelBorder}`,
                borderRadius: '10px',
                fontSize: { xs: 10, md: 12 },
                fontWeight: sc.labelFontWeight,
                color: sc.labelColor,
                textAlign: 'center',
                width: 'max-content',
                maxWidth: { xs: 100, md: 130 },
                minWidth: { xs: 70, md: 90 },
                lineHeight: 1.4,
                pointerEvents: 'none',
                boxShadow:
                  '0 4px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)',
                transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
              }}
            >
              {node.title}
              {currentModuleId &&
                node.module_id !== currentModuleId &&
                node.module_name && (
                  <Box
                    component="div"
                    sx={{
                      mt: 0.5,
                      fontSize: 9,
                      fontWeight: 700,
                      opacity: 0.85,
                      textTransform: 'none',
                    }}
                  >
                    {t('PAL_KC_NODE_FROM_MODULE', {
                      moduleName: node.module_name,
                    })}
                  </Box>
                )}
            </Box>
          </Box>
        );
      })}
    </>
  );
}

export default PalKnowledgeConcepts;
